import ChatDrawer from '@/components/ChatDrawer/ChatDrawer';
import FloatingChatDock from '@/components/ChatDock/FloatingChatDock';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
} from '@/components/ui';
import { AUTHORITIES } from '@/config/constants';
import { useAppDispatch, useAppSelector } from '@/config/store';
import { usePageTitle } from '../../../context/PageTitleContext';
import { SocketContext } from '@/context/socket';
import { hasAnyAuthority } from '@/shared/auth/private-route';
import { IEdoChatMessage } from '@/shared/model/edo-chat-message.model';
import { AuthenticationState } from '@/shared/reducers/authentication';
import { getUnseenMessagesCount } from '@/shared/reducers/entities/edo-chat-message.reducer';
import { EntityState } from '@/shared/reducers/reducer.utils';
import { SettingsState, invertTheme } from '@/shared/reducers/settings.reducer';
import { cn } from '@/shared/util/cn';
import { useMediaQuery } from '@/shared/util/useMediaQuery';
import {
  LogOut,
  Mail,
  Menu,
  Moon,
  Sun,
  Users,
} from 'lucide-react';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Translate } from 'react-jhipster';
import { Link, useNavigate } from 'react-router-dom';
import Drawer, { DRAWER_WIDTH } from './drawer';

interface HeaderProps {
  children?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = (props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const socket = useContext(SocketContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chatDrawer, setChatDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const { unseenMessages } = useAppSelector<EntityState<IEdoChatMessage>>(
    (state) => state.edoChatMessage
  );
  const { theme: themeMode } = useAppSelector<SettingsState>((state) => state.settings);
  const { title, subtitle } = usePageTitle();

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleLogout = () => navigate('/logout');

  const handleOnMessageEvent = () => dispatch(getUnseenMessagesCount());

  useEffect(() => {
    dispatch(getUnseenMessagesCount());
    socket.on('message', handleOnMessageEvent);
    return () => {
      socket.off('message', handleOnMessageEvent)
    };
  }, [dispatch, socket]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const roleLabel = useMemo(() => {
    if (account.roles.some((r) => r.name === AUTHORITIES.ROLE_ADMIN)) return 'Amministratore';
    if (account.roles.some((r) => r.name === AUTHORITIES.ROLE_VICE)) return 'Vicepreside';
    if (account.roles.some((r) => r.name === AUTHORITIES.ROLE_TEACHER)) return 'Docente';
    if (account.roles.some((r) => r.name === AUTHORITIES.ROLE_STUDENT)) return 'Studente';
    if (account.roles.some((r) => r.name === AUTHORITIES.ROLE_PARENT)) return 'Genitore';
    return undefined;
  }, [account.roles]);

  const showChat = hasAnyAuthority(account.roles, [
    AUTHORITIES.ROLE_ADMIN,
    AUTHORITIES.ROLE_SUPPORT_TEACHER,
    AUTHORITIES.ROLE_TEACHER,
    AUTHORITIES.ROLE_VICE,
    AUTHORITIES.ROLE_STUDENT,
  ]);

  return (
    <>
      <ChatDrawer open={chatDrawer} onClose={() => setChatDrawer(false)} />
      <div className="flex min-h-screen flex-col">
        <header
          className={cn(
            'fixed left-0 right-0 top-0 z-30 flex h-16 items-center border-b transition-shadow',
            'bg-page/92 border-[var(--color-mockup-border)]',
            'backdrop-blur-[14px]',
            scrolled && 'shadow-[0_2px_20px_rgba(0,0,0,0.06)]'
          )}
        >
          <div
            className="flex w-full items-center justify-between px-6 md:px-8"
            style={
              isDesktop
                ? { paddingLeft: `calc(${DRAWER_WIDTH}px + 2rem)` }
                : undefined
            }
          >
            <div className="flex items-center gap-3 md:gap-4">
              <IconButton
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                className="md:hidden text-[var(--color-mockup-text-mid)]"
              >
                <Menu className="h-5 w-5" />
              </IconButton>
              <div>
                <h1 className="font-heading text-xl font-black leading-tight text-[var(--color-mockup-text)] md:text-2xl">
                    {title}
                </h1>
                {subtitle && (
                    <p className="mt-0.5 text-xs leading-tight font-medium text-[var(--color-mockup-text-light)]">
                        {subtitle}
                    </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {showChat && (
                <div className="relative">
                  <IconButton
                    aria-label={`${unseenMessages ?? 0} new notifications`}
                    onClick={() => setChatDrawer(true)}
                    className="w-10 h-10 rounded-full border bg-surface text-[var(--color-mockup-text-mid)] hover:bg-primary/10 hover:border-primary/30 hover:text-primary border-[var(--color-mockup-border)]"
                  >
                    <Mail className="h-5 w-5" />
                  </IconButton>
                  {unseenMessages != null && unseenMessages > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[var(--color-mockup-coral)] px-1 text-[10px] font-bold text-white">
                      {unseenMessages > 99 ? '99+' : unseenMessages}
                    </span>
                  )}
                </div>
              )}
              <div className="w-px h-7 bg-[var(--color-mockup-border)]" aria-hidden />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-3 rounded-2xl px-2 py-1.5 text-left hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    aria-haspopup="menu"
                  >
                    <Avatar
                      alt=""
                      size="sm"
                      className="h-9 w-9"
                      fallback={`${account.firstName?.[0] ?? ''}${account.lastName?.[0] ?? ''}`}
                    />
                    <span className="hidden flex-col text-left sm:flex">
                      <span className="text-sm font-bold font-heading text-[var(--color-mockup-text)]">
                        {account.firstName} {account.lastName}
                      </span>
                      {roleLabel && (
                        <span className="text-xs text-[var(--color-mockup-text-light)]">
                          {roleLabel}
                        </span>
                      )}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[200px]">
                  <DropdownMenuItem asChild>
                    <Link to="/account/settings" className="flex cursor-pointer items-center gap-2">
                      <Avatar
                        alt=""
                        size="sm"
                        className="h-8 w-8"
                        fallback={`${account.firstName?.[0] ?? ''}${account.lastName?.[0] ?? ''}`}
                      />
                      <span>
                        {account.firstName} {account.lastName}
                      </span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {showChat && (
                    <DropdownMenuItem asChild>
                      <Link to="/friends" className="cursor-pointer">
                        <Users className="mr-2 h-4 w-4" />
                        Amici
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => dispatch(invertTheme())}
                    className="cursor-pointer"
                  >
                    {themeMode === 'light' ? (
                      <Moon className="mr-2 h-4 w-4" />
                    ) : (
                      <Sun className="mr-2 h-4 w-4" />
                    )}
                    Dark mode
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      handleLogout();
                    }}
                    className="cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <Translate contentKey="global.menu.account.logout">Sign out</Translate>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>
        <Drawer mobileOpen={mobileOpen} onDrawerToggle={handleDrawerToggle} />
        <main className="flex-1 pt-16 md:ml-[256px]">
          {props.children}
        </main>
      </div>
      {showChat && <FloatingChatDock />}
    </>
  );
};

export default Header;
